// import external stuff
import { type App } from 'vue';
import {
    Configuration,
    ProductApi,
    SupplierApi,
    TemplateApi,
} from '@precon-tools-suite-by-shared/gino-importer-client';
import {
    CacheLookupPolicy,
    useMsalStore,
} from '@precon-tools-suite-by-shared/vue-msal';

// import internal stuff
import {
    ginoImportProductsClientInjectionKey,
    ginoImportSuppliersClientInjectionKey,
    ginoImportTemplateClientInjectionKey,
    importApiBaseUrlInjectionKey,
} from './symbols';

import VueMsalMiddleware from './middleware/vue-msal';
import { getImporterBaseUrl } from './helpers/getImporterBaseUrl';
import { getScopes } from './helpers/getScopes';

// exports internal stuff
export * from './composables/index';
export { getImportTemplateUrl } from './helpers/import-templates';

export type GinoImportsOptions = {
    basePath?: string;
    tenantId?: string;
    importApiIdentifier?: string;
};

// The definition of the plugin that installs the
export default {
    /**
     *
     * @param app
     * @param basePath
     * @param tenantId
     */
    install(app: App, options: GinoImportsOptions = {}) {
        const msalStore = useMsalStore();

        const basePath = getImporterBaseUrl(options.basePath);
        app.provide(importApiBaseUrlInjectionKey, basePath);

        const scopes = getScopes(options.importApiIdentifier);

        const resolveToken = async () => {
            const response = await msalStore.acquireToken({
                scopes,
                cacheLookupPolicy: CacheLookupPolicy.RefreshToken, // HACK // TODO - this is temporarily added until we have implemented better subjects on tokens (user and team instead of just user)
            });
            return response.accessToken;
        };

        const config = new Configuration({
            basePath,
            middleware: [new VueMsalMiddleware(resolveToken)],
        });

        const productApi = new ProductApi(config);
        const supplierApi = new SupplierApi(config);
        const templateApi = new TemplateApi(config);

        app.provide(ginoImportProductsClientInjectionKey, productApi);
        app.provide(ginoImportSuppliersClientInjectionKey, supplierApi);
        app.provide(ginoImportTemplateClientInjectionKey, templateApi);
    },
};
